import { Box, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { AnimatedNumber } from '../../../common/components/AnimatedNumber';
import { DataChip } from '../../../common/components/DataChip';
import { TrendInfo } from './TrendInfo';
import { ReportDiffCountMap } from '../models/ReportDiffCountMap';
import { SEVERITY_COLORS, Severity } from '../../../common/enums/Severity';

type Props = {
  currentCountMap: ReportDiffCountMap;
  selectedCountMap: ReportDiffCountMap;
  onActiveIndexChange: (index: number | null) => void;
  activeIndex: number | null;
  type: 'issues' | 'instances';
};

const calculatePercentage = (value: number, total: number) => {
  if (total === 0) {
    return 0;
  }

  return (value / total) * 100;
};

export const PieChartSummary = (props: Props) => {
  const { onActiveIndexChange, activeIndex, selectedCountMap, currentCountMap, type } = props;
  const tabName = type === 'issues' ? t('comparison.tabHeaderIssues') : t('comparison.tabHeaderInstances');

  const rows = [
    {
      name: t('high'),
      amount: currentCountMap.getHigh(),
      percentage: calculatePercentage(currentCountMap.getHigh(), currentCountMap.getTotal()),
      amountDelta: currentCountMap.getHigh() - selectedCountMap.getHigh(),
      percentageDelta: currentCountMap.getHigh() / selectedCountMap.getHigh() - 1,
      isPositiveTrendUp: false,
      ...SEVERITY_COLORS[Severity.HIGH],
    },
    {
      name: t('medium'),
      amount: currentCountMap.getMedium(),
      percentage: calculatePercentage(currentCountMap.getMedium(), currentCountMap.getTotal()),
      amountDelta: currentCountMap.getMedium() - selectedCountMap.getMedium(),
      percentageDelta: currentCountMap.getMedium() / selectedCountMap.getMedium() - 1,
      isPositiveTrendUp: false,
      ...SEVERITY_COLORS[Severity.MED],
    },
    {
      name: t('low'),
      amount: currentCountMap.getLow(),
      percentage: calculatePercentage(currentCountMap.getLow(), currentCountMap.getTotal()),
      amountDelta: currentCountMap.getLow() - selectedCountMap.getLow(),
      percentageDelta: currentCountMap.getLow() / selectedCountMap.getLow() - 1,
      isPositiveTrendUp: false,
      ...SEVERITY_COLORS[Severity.LOW],
    },
    {
      name: t('passed'),
      amount: currentCountMap.getPassed(),
      percentage: calculatePercentage(currentCountMap.getPassed(), currentCountMap.getTotal()),
      amountDelta: currentCountMap.getPassed() - selectedCountMap.getPassed(),
      percentageDelta: currentCountMap.getPassed() / selectedCountMap.getPassed() - 1,
      isPositiveTrendUp: true,
      ...SEVERITY_COLORS[Severity.PASSED],
    },
  ];

  return (
    <Stack direction="column" alignItems="stretch" spacing={1} sx={{ py: 1, width: '100%' }}>
      <Stack direction="row" alignItems="center" gap={0.25}>
        <Typography flex={2} sx={{ px: 1, py: 0.5, backgroundColor: '#D9D9D933', color: 'text.primary', fontSize: '0.75rem' }}>
          {t('comparison.tabHeaderSeverity')}
        </Typography>
        <Typography flex={2} sx={{ px: 1, py: 0.5, backgroundColor: '#D9D9D933', color: 'text.primary', fontSize: '0.75rem' }}>
          {t('comparison.tabHeaderChange')}
        </Typography>
        <Typography flex={1} sx={{ textAlign: 'center', px: 1, py: 0.5, backgroundColor: '#D9D9D933', color: 'text.primary', fontSize: '0.75rem' }}>
          {tabName}
        </Typography>
      </Stack>
      {rows.map(({ name, amount, percentage, text, main, background, amountDelta, percentageDelta, isPositiveTrendUp }, index) => (
        <Stack
          key={index}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 1,
            py: 0.625,
            borderRadius: '4px',
            transition: 'all 0.3s ease-in-out',
            backgroundColor: activeIndex === index ? 'backdrops.grey' : '',
          }}
          spacing={2}
          onClick={() => onActiveIndexChange(index)}
          onMouseEnter={() => onActiveIndexChange(index)}
          onMouseLeave={() => onActiveIndexChange(null)}
        >
          <Stack flex={2} direction="column">
            <Typography variant="subtitle1" component={'div'} sx={{ lineHeight: 1 }}>
              {name}
            </Typography>
            <Typography
              variant="subtitle1"
              component="span"
              sx={{
                color: 'light.base.subtitle',
                mt: 0.125,
                lineHeight: 1.25,
              }}
            >
              <AnimatedNumber value={percentage} suffix={'%'} decimals={2} />
            </Typography>
          </Stack>
          <Stack flex={2} direction="row" gap={1} alignItems="center">
            <TrendInfo percentage={percentageDelta * 100} value={amountDelta} isPositiveTrendUp={isPositiveTrendUp} />
          </Stack>

          <Box flex={1}>
            <DataChip textColor={text} backgroundColor={background} borderColor={main} label={<AnimatedNumber value={amount} />} />
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};
